import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Page from 'components/Page';
import React from 'react';
import { useParams } from 'react-router-dom';
import { routeMap, useNavigate } from 'routeMap';
import {
  HeroIconExclamation,
  usePagination,
  useWrappedUrqlMutation,
} from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import { useGetBankAccountQuery } from './GetBankAccount.generated';
import Card from 'components/Card/Card';
import Table from 'components/Table/Table';
import JcapPagination from 'components/JcapPagination/JcapPagination';
import Stack from 'toolbox/src/components/Stack/Stack';
import InlineTextEdit, {
  useInlineTextEditState,
} from 'components/InlineTextEdit/InlineTextEdit';
import { useUpdateActualBalanceMutation } from './UpdateActualBalance.generated';
import { toast } from 'react-toastify';
import CardWithAside from 'components/Card/CardWithAside';
import { UpdateBankAccountForm } from 'components/AdminViewBankAccounts/forms';
import ResetAndSubmitButton from 'components/ResetAndSubmitButton/ResetAndSubmitButton';
import { useUpdateBankAccountMutation } from './UpdateBankAccount.generated';
import { useRemoveBankAccountMutation } from './RemoveBankAccount.generated';
import DangerButton from 'components/DangerButton/DangerButton';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';

interface ViewBankAccountProps {}

export const ViewBankAccount: React.FC<ViewBankAccountProps> = () => {
  const navigate = useNavigate();
  const { account_id } = useParams<{ account_id: string }>();
  const pagination = usePagination({ resultsPerPage: 20 });
  const [result] = useGetBankAccountQuery({
    variables: {
      account_id: account_id,
      balancesLimit: pagination.limit,
      balancesOffset: pagination.offset,
    },
  });

  const update = useWrappedUrqlMutation(useUpdateBankAccountMutation, {
    onSuccess: (result) => {
      toast.info('Updated bank account successfully.');
      navigate.viewBankAccountById({
        params: {
          account_id: result?.update_bank_accounts_by_pk?.account_id,
        },
      });
    },
  });

  pagination.useWatchAllResultsCount(
    result.data?.bank_accounts_by_pk?.actual_balances_aggregate?.aggregate
      ?.count,
  );

  const deleteBankAccount = useWrappedUrqlMutation(
    useRemoveBankAccountMutation,
    {
      onSuccess: (result) => {
        toast.info('Deleted bank account successfully');
        navigate.viewCurrencyBookById({
          params: {
            id: result?.delete_bank_accounts?.returning?.[0]?.currency_book_id,
          },
        });
      },
      onError: () => {},
    },
  );

  const {
    setWhichInputIdIsBeingEdited,
    whichInputIdIsBeingEdited,
    closeAllInputs,
  } = useInlineTextEditState();

  const updateActualBalance = useWrappedUrqlMutation(
    useUpdateActualBalanceMutation,
    {
      onSuccess: () => {
        toast('Updated account balance successfully');
        closeAllInputs();
      },
      onError: () => {
        toast.error('Something went wrong. Could not update account balance.');
      },
    },
  );

  if (!result.data) {
    return null;
  }

  const bank_account = result.data.bank_accounts_by_pk;

  return (
    <Page
      icon={
        <Flex mx={1} alignItems="center">
          <Breadcrumbs
            links={[
              {
                label:
                  bank_account?.currency_book?.business_unit?.organisation.name,
                link: routeMap.viewOrgById({
                  params: {
                    id:
                      bank_account?.currency_book?.business_unit?.organisation
                        ?.id,
                  },
                }),
              },
              {
                label: bank_account?.currency_book?.business_unit?.name,
                link: routeMap.viewBusinessUnit({
                  params: {
                    id: bank_account?.currency_book?.business_unit?.id,
                  },
                }),
              },
              {
                label: bank_account?.currency_book?.name,
                link: routeMap.viewCurrencyBookById({
                  params: {
                    id: bank_account?.currency_book?.id,
                  },
                }),
              },
              {
                label: bank_account?.account_id,
              },
            ]}
          />
        </Flex>
      }
    >
      <UpdateBankAccountForm.Wrapper
        onSubmit={(values) =>
          update.dispatch({
            account_id: bank_account.account_id,
            modification: {
              account_id: values?.account_id,
              bank_id: values?.bank_id,
              product_type: values?.product_type,
              opening_interest_rate: values?.opening_interest_rate || 0,
              fixed_maturity_date: values?.fixed_maturity_date,
              notice_days: values?.notice_days,
            },
          })
        }
        initialValues={{
          ...bank_account,
          opening_interest_rate: bank_account.opening_interest_rate.toString(),
        }}
      >
        <CardWithAside
          title={'Manage Bank Account'}
          footer={<ResetAndSubmitButton />}
          aside={<p className="text-sm">Edit this bank account's details</p>}
        >
          <UpdateBankAccountForm.Inputs.account_id label="Account ID" />
          <UpdateBankAccountForm.Inputs.bank_id label="Bank" />
          <UpdateBankAccountForm.Inputs.product_type label="Account Type" />
          <UpdateBankAccountForm.Consumer>
            {({ values }) => {
              switch (values.product_type) {
                case 'FIXED':
                  return (
                    <UpdateBankAccountForm.Inputs.fixed_maturity_date label="Fixed Maturity Date" />
                  );
                case 'NOTICE':
                  return (
                    <UpdateBankAccountForm.Inputs.notice_days label="Notice Days" />
                  );
                default:
                  return null;
              }
            }}
          </UpdateBankAccountForm.Consumer>
          <UpdateBankAccountForm.Inputs.opening_interest_rate label="Interest Rate (%AER)" />
        </CardWithAside>
      </UpdateBankAccountForm.Wrapper>
      <Card title="Account Balances">
        <Stack my={2}>
          {bank_account.actual_balances.length > 0 && (
            <>
              <Table
                data={bank_account.actual_balances}
                columns={[
                  {
                    Header: 'Date',
                    accessor: ({ date }) => date,
                  },
                  {
                    Header: 'Balance',
                    accessor: ({ balance }) =>
                      new Intl.NumberFormat('en-UK', {
                        style: 'currency',
                        currency: bank_account.currency.prefix,
                      }).format(balance),
                  },
                  ...(bank_account.currency.prefix !== 'GBP'
                    ? [
                        {
                          Header: 'Balance GBP',
                          accessor: ({ balance_gbp }) =>
                            new Intl.NumberFormat('en-UK', {
                              style: 'currency',
                              currency: 'GBP',
                            }).format(balance_gbp),
                        },
                      ]
                    : []),
                  {
                    Header: 'Interest Rate',
                    accessor: ({ interest_rate, id }) => (
                      <InlineTextEdit
                        label="Interest Rate"
                        setWhichInputIdIsBeingEdited={
                          setWhichInputIdIsBeingEdited
                        }
                        whichInputIdIsBeingEdited={whichInputIdIsBeingEdited}
                        value={interest_rate}
                        onSubmit={({ value }) =>
                          updateActualBalance.dispatch({
                            id,
                            interest_rate: Number(value),
                          })
                        }
                        isLoading={updateActualBalance.fetching}
                      >
                        {interest_rate}%
                      </InlineTextEdit>
                    ),
                  },
                ]}
              />
              <JcapPagination {...pagination} />
            </>
          )}
          {bank_account.actual_balances.length === 0 && (
            <p className="text-sm text-gray-700">
              There have been no balances recorded against this account.
            </p>
          )}
        </Stack>
      </Card>
      <Card title="Delete Bank Account">
        <p className="mb-4 text-sm text-gray-600">
          Deleting the bank account will also delete any balances associated
          with it.
        </p>
        <Stack alignItems="flex-end">
          <DangerButton
            onClick={() =>
              confirm(
                'Are you sure you want to delete this bank account?\n\nAll balances associated with this bank account will also be deleted.',
              ) === true && deleteBankAccount.dispatch({ account_id })
            }
          >
            <Flex mx={2}>
              <HeroIconExclamation />
              <p>Delete</p>
            </Flex>
          </DangerButton>
          <ErrorMessage {...deleteBankAccount} />
        </Stack>
      </Card>
    </Page>
  );
};

export default ViewBankAccount;
